import { Button, Message, Toast } from "@/ui-kit";
import { useTranslation } from "react-i18next";
import { InputPasswordControl } from "@/components/controls";
import { FormProvider, useForm } from "react-hook-form";
import { useCallback, useContext, useMemo, useState } from "react";
import { AuthPageContext } from "../../context/AuthPageContext";
import { useNavigate, useSearchParams } from "react-router";
import { ROUTES } from "@/router";
import { FormView } from "../FormView";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { PasswordRequirementMessage } from "../PasswordRequirementMessage";
import { AuthRepository } from "@/api/auth/repository";
import { AuthService } from "@/api/auth/service";

type IForm = {
  new_password: string;
  repeat_password: string;
};

const ResetPassword = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const navigate = useNavigate();
  const { usernameRef } = useContext(AuthPageContext);
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const authRepository = useMemo(() => {
    return new AuthRepository(new AuthService());
  }, []);

  const schema = useMemo(() => {
    return z
      .object({
        new_password: z
          .string()
          .min(1, { message: t("validation.required") })
          .min(8, { message: t("validation.password_too_short") }),
        repeat_password: z
          .string()
          .min(1, { message: t("validation.required") })
          .min(8, { message: t("validation.password_too_short") }),
      })
      .refine((data) => data.new_password === data.repeat_password, {
        message: t("validation.passwords_must_match"),
        path: ["repeat_password"],
      });
  }, [t]);

  const form = useForm<IForm>({
    defaultValues: {
      new_password: "",
      repeat_password: "",
    },
    resolver: zodResolver(schema),
  });
  const { handleSubmit, formState, watch } = form;
  const password = watch("new_password");

  const onSubmit = useCallback(
    (formData: IForm) => {
      setLoading(true);
      return authRepository
        .resetPassword({
          token: token,
          newPassword: formData.new_password,
        })
        .then(() => {
          Toast.success({
            title: t("toast.password_reset_success_title"),
          });
          const email = usernameRef.current
            ? `?email=${usernameRef.current}`
            : "";
          navigate(`/${ROUTES.AUTH}/${ROUTES.SIGN_IN}${email}`);
        })
        .catch((e: { suberror: string }) => {
          Toast.error({
            title: t("toast.password_reset_error_title"),
            message: t("toast.password_reset_error_message"),
          });
          const suberror = e?.suberror || "invalid_email_or_password";
          setMessage(t(`validation.${suberror}`));
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [authRepository, usernameRef, navigate, t, token],
  );

  const handleBackClick = useCallback(() => {
    navigate(`/${ROUTES.AUTH}/${ROUTES.SIGN_IN}`);
  }, [navigate]);

  return (
    <FormProvider {...form}>
      <FormView title={t("label.create_new_password")}>
        <form
          className="nuco-auth__common-form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <InputPasswordControl
            name="new_password"
            size="lg"
            label={t("label.new_password")}
            placeholder={t("placeholder.password")}
            autoFocus={true}
            invalid={!!formState.errors.new_password}
            color="layer2"
          />

          <InputPasswordControl
            name="repeat_password"
            size="lg"
            label={t("label.repeat_password")}
            placeholder={t("placeholder.password")}
            invalid={!!formState.errors.repeat_password}
            color="layer2"
          />

          {message && (
            <div className="nuco-auth-common-form__message">
              <Message text={message} variant="red" />
            </div>
          )}

          <PasswordRequirementMessage password={password} />

          <div className="nuco-auth__common-form-actions">
            <Button
              type="submit"
              size="lg"
              content={t("action.reset_password")}
              loading={loading}
            />

            <Button
              type="button"
              size="lg"
              color="layer2"
              content={t("action.back")}
              onClick={handleBackClick}
            />
          </div>
        </form>
      </FormView>
    </FormProvider>
  );
};

export default ResetPassword;
